import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CompleteEsignRequest,
  CompleteInksignRequest,
  DocutechAccessRequest,
  DocutechAccessResponse,
  ENVIRONMENT,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class DocutechService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async getDocutechAccess(request: DocutechAccessRequest): Promise<DocutechAccessResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/docutech/auth`;

    try {
      return await firstValueFrom(this.http.post<DocutechAccessResponse>(url, request));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error getting Docutech access for loan ${request.loanId}`);
      throw new Error(`Failed to get Docutech access: ${message}`);
    }
  }

  async getDocumentEmbed(documentId: string): Promise<DocutechAccessResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/documents/${documentId}/embed`;

    try {
      return await firstValueFrom(this.http.get<DocutechAccessResponse>(url));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error getting document embed for ${documentId}`);
      throw new Error(`Failed to get document embed: ${message}`);
    }
  }

  async completeEsign(loanId: string, documentId: string, request: CompleteEsignRequest): Promise<void> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/documents/${documentId}/complete`;

    try {
      await firstValueFrom(this.http.put<void>(url, request));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error completing e-sign for document ${documentId}`);
      throw new Error(`Failed to complete e-sign: ${message}`);
    }
  }

  async completeInksign(loanId: string, documentId: string, request: CompleteInksignRequest): Promise<void> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/documents/${documentId}/complete`;

    try {
      await firstValueFrom(this.http.put<void>(url, request));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error completing ink-sign for document ${documentId}`);
      throw new Error(`Failed to complete ink-sign: ${message}`);
    }
  }
}
